import { ExpandableEvent } from "../events/ExpandableEvent.js";


/**
 * Class to implement the expandable functionality
 * for a single instance; the application is done
 * via the ExpandableManager class
 */
export class Expandable
{
    private identifier: string | null = null;
    private content: HTMLDivElement | null = null;
    private button: HTMLButtonElement | null = null;
    private label: HTMLSpanElement | null = null;

    /**
     * Constructor of the Expandable
     *
     * @param {HTMLElement} scope
     */
    public constructor(scope: HTMLElement)
    {
        this.button = scope.querySelector<HTMLButtonElement>(`:scope > button:last-of-type`);
        this.identifier = this.button?.getAttribute("aria-controls") ?? null;
        this.content = scope.querySelector<HTMLDivElement>(`#${CSS.escape(this.identifier ?? "")}`);
        this.label = this.button?.querySelector<HTMLSpanElement>("span") ?? null;

        this.useMouseClick();
        this.useStateUpdates();
    }

    /**
     * Handles the state of the expandable
     * content via mouse-click
     *
     * @returns {void}
     */
    protected useMouseClick(): void
    {
        this.button?.addEventListener("click", () => {
            switch (this.button?.getAttribute("aria-expanded"))
            {
                case "true":
                    document.dispatchEvent(new ExpandableEvent({
                        detail: {
                            action: "close",
                            identifier: this.identifier
                        }
                    }));
                    break;

                case "false":
                    document.dispatchEvent(new ExpandableEvent({
                        detail: {
                            action: "open",
                            identifier: this.identifier
                        }
                    }));
                    break;
            }
        });
    }

    /**
     * Handles updates to the state of the target
     * element and reflects them on all elements
     *
     * @returns {void}
     */
    protected useStateUpdates(): void
    {
        document.addEventListener(ExpandableEvent.KEY, event => {
            if (event instanceof ExpandableEvent) {
                const { action, identifier } = event.detail;
                if (!this.identifier || this.identifier !== identifier) return;

                switch (action)
                {
                    case "open":
                        this.button?.setAttribute("aria-expanded", "true");
                        if (this.content) this.content.hidden = false;

                        if (this.label) {
                            this.label.innerText = this.button?.dataset.labelCollapse ?? "";
                        }
                        break;

                    case "close":
                        this.button?.setAttribute("aria-expanded", "false");
                        if (this.content) this.content.hidden = true;

                        if (this.label) {
                            this.label.innerText = this.button?.dataset.labelExpand ?? "";
                        }
                        break;
                }
            }
        });
    }
}
